import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Plus, Building2, User as UserIcon, Trash2 } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import { estadoLabel, tipoDocLabel } from "@/lib/format";
import { tipoDocOptions } from "@/config/documentSchemas";
import { MontoOperacion, HistorialEstados } from "@/components/entities/OperacionExtras";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function OperacionPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedTipo, setPickedTipo] = useState("");
  const [activos, setActivos] = useState(null);
  useEffect(() => { api.get("/tipos-contrato/activos").then((r) => setActivos(r.data)).catch(() => setActivos(null)); }, []);
  const opciones = tipoDocOptions.filter((t) => !activos || activos.includes(t.value));

  const load = async () => {
    try {
      const { data } = await api.get(`/operaciones/${id}`);
      setInfo(data);
    } catch (e) {
      toast.error("No se pudo cargar la operación");
      nav("/app");
    }
  };
  useEffect(() => { load(); }, [id]);

  const cambiarEstado = async (estado) => {
    try {
      await api.put(`/operaciones/${id}`, { estado });
      toast.success("Estado actualizado");
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  const eliminar = async () => {
    if (!window.confirm("¿Eliminar operación y sus documentos?")) return;
    await api.delete(`/operaciones/${id}`);
    toast.success("Operación eliminada");
    nav("/app");
  };

  const startDoc = () => {
    if (!pickedTipo) return toast.error("Elige un tipo de documento");
    nav(`/app/operaciones/${id}/nuevo/${pickedTipo}`);
  };

  const eliminarDoc = async (did) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    await api.delete(`/documentos/${did}`);
    toast.success("Documento eliminado");
    load();
  };

  if (!info) return (
    <div className="min-h-screen"><AppHeader />
      <div className="text-center py-20 text-slate-500">Cargando…</div>
    </div>
  );

  const op = info.operacion;
  const docs = info.documentos || [];

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-dashboard-link">
          <ArrowLeft className="w-4 h-4" /> Todas las operaciones
        </Link>

        <div className="bm-card p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <span className={`bm-badge bm-badge-${op.estado} mb-3`}>{estadoLabel[op.estado]}</span>
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900" data-testid="op-title">{op.titulo}</h1>
              {op.notas && <p className="text-slate-600 mt-2">{op.notas}</p>}
            </div>
            <div className="flex flex-col gap-2 min-w-[220px]">
              <Select value={op.estado} onValueChange={cambiarEstado}>
                <SelectTrigger data-testid="op-estado-select"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospecto">Prospecto</SelectItem>
                  <SelectItem value="oferta">Oferta</SelectItem>
                  <SelectItem value="promesa">Promesa</SelectItem>
                  <SelectItem value="cierre">Cierre</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={eliminar} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1 justify-center" data-testid="op-delete-btn">
                <Trash2 className="w-4 h-4" /> Eliminar operación
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6 pt-6 border-t border-[#E2DCD2]">
            <div className="p-3 rounded-xl bg-[#F3EFE6]/50">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><UserIcon className="w-3 h-3" /> Propietario</div>
              <div className="text-sm text-slate-800 mt-1 font-medium">{info.propietario?.nombre || info.propietario?.razon_social || "—"}</div>
            </div>
            <div className="p-3 rounded-xl bg-[#F3EFE6]/50">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><UserIcon className="w-3 h-3" /> Comprador/Inquilino</div>
              <div className="text-sm text-slate-800 mt-1 font-medium">{info.contraparte?.nombre || info.contraparte?.razon_social || "—"}</div>
            </div>
            <div className="p-3 rounded-xl bg-[#F3EFE6]/50">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Inmueble</div>
              <div className="text-sm text-slate-800 mt-1 font-medium line-clamp-2">{info.inmueble?.direccion || "—"}</div>
            </div>
            <MontoOperacion op={op} onSaved={load} />
          </div>
        </div>
        <HistorialEstados items={op.historial_estados} />

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-bold text-slate-900">Documentos</h2>
          <button onClick={() => setPickerOpen(true)} className="bm-btn-primary text-sm min-h-[44px]" data-testid="new-doc-btn">
            <Plus className="w-4 h-4" /> Nuevo documento
          </button>
        </div>

        {docs.length === 0 ? (
          <div className="bm-card p-8 text-center" data-testid="docs-empty">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-600">Aún no hay documentos en esta operación.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.id} className="bm-card p-4 flex items-center justify-between gap-3" data-testid={`doc-row-${d.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#E7EBF3] text-[#1E3A5F] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-slate-900 truncate">{d.titulo}</div>
                    <div className="text-xs text-slate-500">{tipoDocLabel[d.tipo]} · {d.estado === "listo" ? "Listo" : "Borrador"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => nav(`/app/documentos/${d.id}`)} className="text-sm px-3 py-2 rounded-lg text-[#2A5C55] font-semibold hover:bg-[#EDF5F3]" data-testid={`doc-open-${d.id}`}>
                    Abrir
                  </button>
                  <button onClick={() => eliminarDoc(d.id)} className="text-slate-400 hover:text-red-600 p-2" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="doc-picker-dialog">
          <DialogHeader>
            <DialogTitle>¿Qué documento vas a generar?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {opciones.map((t) => (
              <button key={t.value} onClick={() => setPickedTipo(t.value)} data-testid={`pick-tipo-${t.value}`}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${pickedTipo === t.value ? "border-[#1E3A5F] bg-[#E7EBF3]" : "border-[#E2DCD2] hover:border-[#2A5C55]/40"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${t.color}18`, color: t.color }}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="font-semibold text-slate-900">{t.label}</div>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setPickerOpen(false)} className="text-slate-600 px-4 py-2">Cancelar</button>
            <button onClick={startDoc} className="bm-btn-primary" data-testid="doc-picker-start">Comenzar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
